import { TrackJS } from "trackjs";

import * as Vue from "vue";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { createPinia } from "pinia";
import global from "./plugins";
import { useMetrics } from "./helpers/metrics";
import { isOnQAOrLocal } from "./helpers";

// Expose Vue globally for CKEditor UMD modules
if (typeof window !== "undefined") {
  (window as any).Vue = Vue;
}

import { useUserStore } from "./store/userStore";
import { useAdminStore } from "./store/adminStore";
import { useAlertStore } from "./store/alertStore";
import { useBldgStore } from "./store/bldgStore";
import { usePersonStore } from "./store/personStore";
import { useRespondStore } from "./store/respondStore";
import { useTrainingStore } from "./store/trainingStore";
import { useReportStore } from "./store/reportStore";

import "./index.css";

const safetyNetVersion = import.meta.env.VITE_SAFETYNET_VERSION || "unknown";
console.log("Loading SafetyNet version " + safetyNetVersion);

if (!isOnQAOrLocal()) {
  TrackJS.install({
    token: "d065b70e634c43febed1b53cf98b73aa",
    version: safetyNetVersion,
    network: { error: false },
    window: { promise: false },
    //console: { display: isOnQAOrLocal() }, // Keeps console logs visible on qa, but not prod
    // Keep console logs visible on production for now
    //  since we are using them to log additional error details that are helpful for debugging. 
    // We can revisit this in the future if needed.
    console: { display: true }, 
    forwardingDomain: "errors.aksafetynet.com",
  });
  TrackJS.addMetadata("client_version", safetyNetVersion);
}

const app = createApp(App).use(router).use(global);
const store = createPinia();

const metrics = useMetrics();

//Run metrics only on production
if (!isOnQAOrLocal()) {
  app.use(metrics);
}

app.use(store);

// Initialize stores after Pinia is installed
const userStore = useUserStore();
const adminStore = useAdminStore();
const alertStore = useAlertStore();
const bldgStore = useBldgStore();
const personStore = usePersonStore();
const respondStore = useRespondStore();
const trainingStore = useTrainingStore();
const reportStore = useReportStore();

if (import.meta.env.DEV) {
  if (window.__VUE_DEVTOOLS_GLOBAL_HOOK__) {
    window.__VUE_DEVTOOLS_GLOBAL_HOOK__.Vue = app;
  }
  (window as any).app = app;
  (window as any).store = store;

  (window as any).bldgStore = bldgStore;
  (window as any).userStore = userStore;
}

tryGetUser().then(() => {
  app.mount("#app");
});

app.config.errorHandler = (err, vm, info) => {
  if (import.meta.env.PROD) {
    // Get component information safely for Vue 3
    const componentName = vm?.$?.type?.name || vm?.$?.type?.__name || 'Unknown Component';
    const componentProps = vm?.$?.props || {};

    // Log properties passed to the component if there are any
    if (Object.keys(componentProps).length > 0) {
      TrackJS.console.log("Props passed to component", componentProps);
    }

    // Emit component name and also the lifecycle hook the error occurred in if present
    var infoMessage = `Error in component: <${componentName} />\n`;
    if (info) {
      infoMessage += `Error occurred during lifecycle hook: ${info}\n`;
    }

    // This puts the additional error information in the Telemetry Timeline
    TrackJS.console.log(infoMessage);

    // Track the native JS error
    TrackJS.track(err as Error);
  }
  
  // Always log to console in development
  if (import.meta.env.DEV) {
    console.error('Vue Error:', err);
    console.error('Component:', vm);
    console.error('Info:', info);
  }
};

// Try to pull user object from LocalStorage before App mounts
async function tryGetUser() {
  try {
    var siteCode = location.pathname?.split("/")[1]?.toLowerCase().trim();
    var nonSiteCodePaths = [
      "login",
      "forgotpassword",
      "resetpassword",
      "client",
      "portallist",
      "content",
    ];

    //Don't try to load the user if this is the login page && 
    if (!siteCode || siteCode.length == 0 || nonSiteCodePaths.includes(siteCode)) {
      return;
    }

    let params = new URLSearchParams(window.location.search);
    let urlToken = params.get("authtoken") || params.get("token");
    let authToken = userStore.loadAuthToken(siteCode);
    if (urlToken) {
      //Remove token from URL
      //window.history.replaceState(null, "", location.pathname);

      if (!authToken) {
        console.log("Found user token in url params");
        userStore.setAuthToken(urlToken);
        authToken = urlToken;
      }
    }

    if (userStore.authToken) {
      adminStore.setAuthToken(authToken!);
      alertStore.setAuthToken(authToken!);
      bldgStore.setAuthToken(authToken!);
      personStore.setAuthToken(authToken!);
      respondStore.setAuthToken(authToken!);
      trainingStore.setAuthToken(authToken!);
      reportStore.setAuthToken(authToken!);
      if (location?.pathname?.startsWith("/staff")) {
      }
    }

    let user = await userStore.getUser(siteCode, 0, true);
    if (user) {
      console.log("Main.js found user", user);
    } else {
      console.log("main.tryGetUser> No local user found.");
    }
  } catch (error) {
    console.error("Error in tryGetUser:", error);
    if (!isOnQAOrLocal()) {
      TrackJS.track(error as Error);
    }
  }
}
